import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { pageConfig } from '../config/page-titles';
import ABComparisonSlider from '../components/ABComparisonSlider';
import { SimpleResponsivePicture, SimpleResponsiveImage } from '../components/SimpleResponsiveImage';
import ImageCycle from '../components/ImageCycle';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../utils/examples.css';
import GlyphSvg from '../components/Glyphs';

// Register ScrollTrigger
gsap.registerPlugin(ScrollTrigger);

export default function Examples() {
  
  usePageTitle({
    title: pageConfig.examples.title,
    description: pageConfig.examples.description,
    image: pageConfig.examples.image
  });

  const [selectedBrand, setSelectedBrand] = useState('umber-ochre');
  const [isLoading, setIsLoading] = useState(true);
  const galleryRef = useRef();

  // Sample data for examples with expanded gallery content
  const brands = [
    {
      id: 'umber-ochre',
      logo: 'img/examples/uando/uando_logo.png',
      logoWidth: 250,
      brand: 'Umber & Ochre',
      headline: 'Avatar Swap Demo',
      typeOfProduct: 'Lifestyle Apparel',
      typeOfBusiness: 'Ecommerce',
      services: [
        { glyph: 'g09', text: 'Model Swap' },
        { glyph: 'g11', text: 'Avatar Generation' },
        { glyph: 'g04', text: 'Diverse Representation' },
        { glyph: 'g05', text: 'Pose Variations' }
      ],
      description: 'With rising costs for model shoots, shipping products back and forth, Umber & Ochre needed a way to reduce costs and increase efficiency. We helped them by using AI to swap models into existing photos from a single model, and generate avatars from reference images.',
      gallery: [
        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/uando/compare_2/2f184600-15c6-4d8a-be16-0badec1d5b22-large-1440w.webp_00009_b.png',
          afterImage: 'img/examples/uando/compare_2/2f184600-15c6-4d8a-be16-0badec1d5b22-large-1440w.webp_00009_.png',
          // caption: 'Generated avatar replacement, real image to AI',
          minHeight: '75vh'
        },

        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/uando/compare_2/2f184600-15c6-4d8a-be16-0badec1d5b22-large-1440w.webp_00010_b.png',
          afterImage: 'img/examples/uando/compare_2/2f184600-15c6-4d8a-be16-0badec1d5b22-large-1440w.webp_00010_.png',
          // caption: 'Generated avatar replacement, real image to AI',
          minHeight: '75vh'
        },

        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/uando/compare_2/DSCF2051_A_0001.webp',
          afterImage: 'img/examples/uando/compare_2/DSCF2051_B_0001.webp',
          // caption: 'Generated avatar replacement, real image to AI',
          minHeight: '75vh'
        },

        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/uando/compare_2/2f184600-15c6-4d8a-be16-0badec1d5b22-large-1440w.webp_00005_b.png',
          afterImage: 'img/examples/uando/compare_2/2f184600-15c6-4d8a-be16-0badec1d5b22-large-1440w.webp_00005_.png',
          // caption: 'Generated avatar replacement, real image to AI',
          minHeight: '75vh'
        },
        
      ]
    },
    {
      id: 'mary-young',
      logo: '/img/examples/my/my_logo.png',
      logoWidth: 100,
      brand: 'Mary Young',
      headline: 'Reducing Intimates Add-on Costs',
      typeOfProduct: 'Lingerie & Intimates',
      typeOfBusiness: 'Ecommerce',
      services: ['Model Swap', 'Avatar Generation', 'Diverse Representation', 'Pose Variations'],
      description: 'Created diverse model representations for their seasonal campaign, significantly reduced photoshoot costs incurred for lingerie modeling.',
      gallery: [
        {
          type: 'ab-slider',
          size: 'L',
          beforeImage: 'img/examples/my/my_a.png',
          afterImage: 'img/examples/my/my_b.png',
          caption: 'Generated avatar replacement, real image to AI'
        },
        {
          type: 'image',
          size: 'S',
          src: 'img/examples/my/new_pose/new_poses_00001_.png',
          caption: 'Alternative generated side angle pose, AI to AI'
        },
        {
          type: 'image',
          size: 'S',
          src: 'img/examples/my/new_pose/new_poses_00002_.png',
          caption: 'Alternative generated front angle pose, AI to AI'
        },
        {
          type: 'ab-slider',
          size: 'L',
          beforeImage: 'img/examples/my/Mary-Young-SS191758-a.png',
          afterImage: 'img/examples/my/Mary-Young-SS191758-b.png',
          caption: 'Generated avatar replacement, real image to AI'
        },
        {
          type: 'image',
          size: 'S',
          src: 'img/examples/my/new_pose2/new_poses_00001_.png',
          caption: 'Alternative generated side angle pose, AI to AI'
        },
        {
          type: 'image',
          size: 'S',
          src: 'img/examples/my/new_pose2/new_poses_00002_.png',
          // caption: 'Additional campaign shot 1'
        },
        {
          type: 'cycle',
          size: 'L',
          images: [
            'img/examples/my/cycle/_yoga_fit_a.png',
            'img/examples/my/cycle/_yoga_fit_b.png',
            'img/examples/my/cycle/_yoga_fit_c.png',
          ],
          captions: [
            'Avatar diversification variants',
          ]
        },
      ]
    },
    {
      id: 'stil',
      logo: '/img/examples/stil/stil_logo.png',
      logoWidth: 140,
      brand: 'STIL',
      headline: 'Staff model stand-in',
      typeOfProduct: 'Stationery & Organization',
      typeOfBusiness: 'Ecommerce',
      services: ['Model Swap', 'Avatar Generation', 'Pose Variations'],
      description: 'For STIL\'s small products, using their staff in on-the-go photoshoots easy but using the same models over time gets stale. We helped diversify their content, keeping photoshoots simple and reducing costs otherwise spent on hiring models.',
      gallery: [
        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/stil/STIL_cherry_a.png',
          afterImage: 'img/examples/stil/STIL_cherry_b.png',
          caption: 'Generated avatar replacement, real image to AI',
          minHeight: '75vh'
        },
        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/stil/STIL_cherry2_a.png',
          afterImage: 'img/examples/stil/STIL_cherry2_b.png',
          caption: 'Generated avatar replacement, real image to AI',
          minHeight: '75vh'
        },
        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/stil/STIL-seude-a.png',
          afterImage: 'img/examples/stil/STIL-seude-b.png',
          caption: 'Generated avatar replacement, real image to AI',
          minHeight: '90vh'
        },
        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/stil/STIL-seude-c.png',
          afterImage: 'img/examples/stil/STIL-seude-d.png',
          caption: 'Generated avatar replacement, real image to AI',
          minHeight: '90vh'
        },
        {
          type: 'ab-slider',
          size: 'L',
          beforeImage: 'img/examples/stil/varipose/varipose_a.png',
          afterImage: 'img/examples/stil/varipose/varipose_b.png',
          caption: 'Generated dozens of variations from real image to AI'
        },
        {
          type: 'ab-slider',
          size: 'S',
          beforeImage: 'img/examples/stil/STIL-leopard_a.jpg',
          afterImage: 'img/examples/stil/STIL-leopard_b.jpg',
          caption: 'Generated avatar replacement, real image to AI',
          minHeight: '90vh'
        },
        {
          type: 'cycle',
          size: 'S',
          images: [
            'img/examples/stil/cycle/new_poses_00001_.png',
            'img/examples/stil/cycle/new_poses_00002_.png',
            'img/examples/stil/cycle/new_poses_00003_.png',
            'img/examples/stil/cycle/new_poses_00004_.png',
            'img/examples/stil/cycle/new_poses_00005_.png',
            'img/examples/stil/cycle/new_poses_00037_.png',
          ],
          captions: [
            'Avatar diversification variants',
          ]
        }
      ]
    }
  ];

  const currentBrand = brands.find(brand => brand.id === selectedBrand) || brands[0];

  // Setup GSAP ScrollTrigger animations
  useEffect(() => {
    if (!galleryRef.current) return;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Kill existing ScrollTriggers
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());

      // Set initial state for all gallery items
      const galleryItems = galleryRef.current.querySelectorAll('.gallery-item');
      
      if (galleryItems.length === 0) return;

      gsap.set(galleryItems, {
        opacity: 0,
        y: 30
      });

      // Create ScrollTrigger for each gallery item
      galleryItems.forEach((item, index) => {
        ScrollTrigger.create({
          trigger: item,
          start: "top 90%",
          end: "bottom 10%",
          onEnter: () => {
            gsap.to(item, {
              opacity: 1,
              y: 0,
              duration: 1,
              delay: index * 0.1,
              ease: "power1.out"
            });
          }
        });
      });

      // Refresh ScrollTrigger to recalculate positions
      ScrollTrigger.refresh();
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [currentBrand, selectedBrand]);

  // Set loading to false after component mounts
  useEffect(() => {
    setIsLoading(false);
  }, []);

    const GalleryItem = ({ item, index }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);

    const handleFullscreenToggle = useCallback(() => {
      if (!currentBrand) return;
      const container = document.querySelector(`[data-gallery-item="${currentBrand.id}-${index}"]`);
      if (!container) return;

      if (!isFullscreen) {
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    }, [isFullscreen, currentBrand.id, index]);

    useEffect(() => {
      function fullscreenChangeHandler() {
        const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
        setIsFullscreen(!!fullscreenElement);
      }
      document.addEventListener('fullscreenchange', fullscreenChangeHandler);
      document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
      document.addEventListener('mozfullscreenchange', fullscreenChangeHandler);
      document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);
      return () => {
        document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
        document.removeEventListener('webkitfullscreenchange', fullscreenChangeHandler);
        document.removeEventListener('mozfullscreenchange', fullscreenChangeHandler);
        document.removeEventListener('MSFullscreenChange', fullscreenChangeHandler);
      };
    }, []);

    switch (item.type) {
      case 'ab-slider':
        return (
          <div key={index} className="gallery-item">
            <ABComparisonSlider
              id={`${currentBrand.id}-${index}`}
              beforeImage={item.beforeImage}
              afterImage={item.afterImage}
              beforeLabel="Original"
              afterLabel="Avatar"
              aspectRatio={item.aspectRatio}
              borderRadius="var(--border-radius)"
              caption={item.caption}
              minHeight={item.minHeight}
            />
          </div>
        );
      case 'image':
        return (
          <div key={index} className="gallery-item">
            <div 
              data-gallery-item={`${currentBrand.id}-${index}`}
              style={{
                position: 'relative',
                width: '100%',
                height: '60vh',
                minHeight: item.minHeight || (item.size === 'S' ? '80vh' : '50vh'),
                maxHeight: '80vh',
                overflow: 'hidden',
                borderRadius: 'var(--border-radius)'
              }}
            >
              <SimpleResponsivePicture
                src={item.src}
                alt={item.caption}
                className="img-fluid w-100 h-100"
                imgStyle={{ 
                  borderRadius: 'var(--border-radius)',
                  objectFit: 'cover',
                  objectPosition: 'top center',
                  width: '100%',
                  height: '100%',
                  maxHeight: '80vh'
                }}
                sources={[
                  { media: "(min-width: 1024px)", widths: [800, 1200, 1600] },
                  { media: "(min-width: 640px)", widths: [600, 800, 1000] },
                  { media: "(max-width: 639px)", widths: [400, 600, 800] }
                ]}
                quality={85}
              />
              <button
                className={`slider-fullscreen-btn${isFullscreen ? ' active' : ''}`}
                type="button"
                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                onClick={handleFullscreenToggle}
                tabIndex={0}
                style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 20, padding: '0.35rem' }}
              >
                <GlyphSvg color={"var(--c0)"} glyph={'g06'} size={"20"} />
              </button>
            </div>
            {item.caption && (
              <p className="fs-8 text-muted mt-2 text-center">{item.caption}</p>
            )}
          </div>
        );
      case 'cycle':
        return (
          <div key={index} className="gallery-item">
            <div 
              data-gallery-item={`${currentBrand.id}-${index}`}
              style={{ position: 'relative' }}
            >
              <ImageCycle
                images={item.images}
                captions={item.captions}
                interval={3000}
                size={item.size}
                minHeight={item.minHeight}
              />
              <button
                className={`slider-fullscreen-btn${isFullscreen ? ' active' : ''}`}
                type="button"
                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                onClick={handleFullscreenToggle}
                tabIndex={0}
                style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 20, padding: '0.35rem' }}
              >
                <GlyphSvg color={"var(--c0)"} glyph={'g06'} size={"20"} />
              </button>
            </div>
            {item.captions && item.captions.length > 0 && (
              <p className="fs-8 text-muted mt-2 text-center">{item.captions[0]}</p>
            )}
          </div>
        );
      case 'video':
        return (
          <div key={index} className="gallery-item">
            <div 
              data-gallery-item={`${currentBrand.id}-${index}`}
              style={{
                position: 'relative',
                width: '100%',
                height: '60vh',
                minHeight: item.minHeight || (item.size === 'S' ? '80vh' : '50vh'),
                maxHeight: '80vh',
                overflow: 'hidden',
                borderRadius: 'var(--border-radius)'
              }}
            >
              <video
                src={item.src}
                controls
                className="w-100 h-100"
                loading="lazy"
                style={{ 
                  borderRadius: 'var(--border-radius)',
                  objectFit: 'cover',
                  objectPosition: 'top center',
                  width: '100%',
                  height: '100%',
                  maxHeight: '80vh'
                }}
              />
              <button
                className={`slider-fullscreen-btn${isFullscreen ? ' active' : ''}`}
                type="button"
                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                onClick={handleFullscreenToggle}
                tabIndex={0}
                style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 20, padding: '0.35rem' }}
              >
                <GlyphSvg color={"var(--c0)"} glyph={'g06'} size={"20"} />
              </button>
            </div>
            {item.caption && (
              <p className="fs-8 text-muted mt-2 text-center">{item.caption}</p>
            )}
          </div>
        );
      case 'static':
        const isVideo = item.src && (item.src.endsWith('.mp4') || item.src.endsWith('.webm') || item.src.endsWith('.mov'));
        return (
          <div key={index} className="gallery-item">
            <div 
              data-gallery-item={`${currentBrand.id}-${index}`}
              style={{
                position: 'relative',
                width: '100%',
                height: item.minHeight || '60vh',
                minHeight: item.minHeight || (item.size === 'S' ? '50vh' : '60vh'),
                maxHeight: item.maxHeight || '80vh',
                overflow: 'hidden',
                borderRadius: 'var(--border-radius)'
              }}
            >
              {isVideo ? (
                <video
                  src={item.src}
                  autoPlay={item.autoPlay !== false}
                  loop={item.loop !== false}
                  muted={item.muted !== false}
                  playsInline
                  controls={item.controls === true}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: item.objectFit || 'cover',
                    objectPosition: item.objectPosition || 'center',
                    borderRadius: 'var(--border-radius)'
                  }}
                />
              ) : (
                <img
                  src={item.src}
                  alt={item.caption || 'Gallery media'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: item.objectFit || 'cover',
                    objectPosition: item.objectPosition || 'center',
                    borderRadius: 'var(--border-radius)'
                  }}
                />
              )}
              <button
                className={`slider-fullscreen-btn${isFullscreen ? ' active' : ''}`}
                type="button"
                aria-label={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
                onClick={handleFullscreenToggle}
                tabIndex={0}
                style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 20, padding: '0.35rem' }}
              >
                <GlyphSvg color={"var(--c0)"} glyph={'g06'} size={"20"} />
              </button>
            </div>
            {item.caption && (
              <p className="fs-8 text-muted mt-2 text-center">{item.caption}</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const renderGallery = () => {
    if (!currentBrand || !currentBrand.gallery) {
      return [];
    }
    const items = currentBrand.gallery;
    const renderedItems = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const size = item.size || 'L'; // Default to large if no size specified
      
      if (size === 'L') {
        // Large items get full width
        renderedItems.push(
          <div key={`row-${i}`} className="mb-4">
            <GalleryItem item={item} index={i} />
          </div>
        );
      } else if (size === 'S') {
        // Small items - check if next item is also small to group them
        if (i + 1 < items.length && items[i + 1].size === 'S') {
          // Create a row with two small items (responsive: 1 column on mobile, 2 on desktop)
          renderedItems.push(
            <div key={`row-${i}`} className="mb-4 gallery-small-pair">
              <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
                <GalleryItem item={item} index={i} />
              </div>
              <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
                <GalleryItem item={items[i + 1]} index={i + 1} />
              </div>
            </div>
          );
          i++; // Skip the next item since we've already rendered it
        } else {
          // Single small item - full width on mobile, half width centered on desktop
          renderedItems.push(
            <div key={`row-${i}`} className="mb-4 gallery-single-small">
              <div className="gallery-item" style={{ width: '100%', overflow: 'hidden' }}>
                <GalleryItem item={item} index={i} />
              </div>
            </div>
          );
        }
      }
    }
    
    return renderedItems;
  };

  return (
    <>
      <section className="container-fluid examples-page">
        <div className="py-4 py-lg-4 py-md-5"></div>
        
        {/* Brand Selector */}
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12">
              <div className="brand-selector d-flex flex-wrap justify-content-center align-items-center gap-3">
                {brands.map((brand) => (
                  <button
                    key={brand.id}
                    onClick={() => setSelectedBrand(brand.id)}
                    className={`brand-logo-btn p-2 border-0 bg-transparent ${
                      selectedBrand === brand.id ? 'selected' : ''
                    }`}
                    style={{
                      transition: 'opacity 0.3s ease',
                      opacity: selectedBrand === brand.id ? 1 : 0.4
                    }}
                  >
                    <img
                      src={brand.logo}
                      alt={`${brand.brand} logo`}
                      className="img-fluid"
                      style={{
                        maxWidth: `${Math.min(brand.logoWidth * 0.6, 80)}px`,
                        height: 'auto',
                        maxHeight: '40px'
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Split Layout */}
        {!isLoading && currentBrand && (
          <div className="container-fluid">
            <div className="row">
              {/* Left Column - Fixed Brand Info */}
              <div className="col-12 col-lg-4 order-1 order-lg-1 d-flex align-items-start">
                <div className="brand-info-fixed p-4" style={{ position: 'sticky', top: '100px' }}>
                  <div className="mb-4">
                    <h2 className="fs-1 c-c00 f-a mb-0"><GlyphSvg glyph={'g01'} size={"14"} color={"var(--c00)"} /> {currentBrand.brand}</h2>
                  </div>
                  
                  <div className="brand-details mb-4">
                    <div className="d-flex flex-column">
                      <p className="fs-8 mb-1 f-b f-uc c-c2">{currentBrand.typeOfProduct}</p>
                      <p className="fs-8 mb-0 f-b f-uc c-c2">{currentBrand.typeOfBusiness}</p>
                    </div>
                  </div>

                  <h4 className="mb-4 f-b c-c00">{currentBrand.headline}</h4>
                  <p className="fs-8 text-muted mb-4">{currentBrand.description}</p>
                  
                  <div>
                    <strong className="fs-8 f-b c-c2">Services provided:</strong>
                    <div className="mt-2">
                      {currentBrand.services.map((service, serviceIndex) => {
                        const serviceText = typeof service === 'string' ? service : service.text;
                        const serviceGlyph = typeof service === 'object' && service.glyph ? service.glyph : null;
                        
                        return (
                          <span
                            key={serviceIndex}
                            className="f-b f-uc"
                            style={{
                              fontSize: '0.65rem',
                              padding: '0.375rem 0.75rem',
                              borderRadius: 'var(--border-radius)',
                              border: '1px solid var(--c00)',
                              color: 'var(--c00)',
                              display: 'inline-block',
                              marginRight: '0.5rem',
                              marginBottom: '0.5rem',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {serviceGlyph && (
                              <GlyphSvg 
                                glyph={serviceGlyph} 
                                size={"10"} 
                                color={"var(--c00)"} 
                                style={{ marginRight: '0.5rem', verticalAlign: 'middle', marginTop: '-3px' }}
                              />
                            )}
                            {serviceText}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Gallery */}
              <div className="col-12 col-lg-8 order-2 order-lg-2 p-4">
                <div ref={galleryRef} className="gallery-container">
                  {renderGallery()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="container mt-5">
          <div className="row justify-content-center">
            <div className="col-12 text-center">
              <h4 className="mb-4 f-uc">Ready to start iterating?</h4>
              <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                <a className="btn b-c5 c-c0" href="/contact" data-discover="true">
                  Start Your Project
                </a>
                <a className="btn b-c0 c-c00 border border-c5" href="/pricing" data-discover="true">
                  View Pricing
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
} 